// Crypto Wallets → Configuration → Gas Funding Policies (SM-278).
// One policy per network, listed so coverage (and gaps) are visible at
// a glance. Create excludes networks that already have a policy — the
// uniqueness rule teaches itself — and the backend 409s as the real
// guard. Edit locks the network so a save can only touch its own row.
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { FormModal } from "@/components/ui/FormModal";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import { TokenAmountInput } from "@/components/ui/TokenAmountInput";
import {
  EnabledToggle,
  errMsg,
  td,
  th,
  useCanConfig,
} from "@/components/wallet-ops/configShared";
import {
  CoverageLine,
  PolicyLoadError,
  PolicyPageHeader,
  PolicyStatusBadge,
  RuleAmount,
  UncoveredCallout,
} from "@/components/wallet-ops/policyShared";
import {
  createGasConfig,
  fetchCryptoAssets,
  fetchCryptoNetworks,
  fetchGasConfigs,
  putGasConfig,
  walletKeys,
} from "@/lib/wallet-queries";
import type {
  CryptoAsset,
  CryptoGasConfig,
  CryptoNetwork,
} from "@/lib/wallet-types";
import { fmtRelative } from "@/lib/format";

export const Route = createFileRoute("/_app/wallet-ops/config/policies/gas")({
  component: GasPoliciesPage,
});

type EditorState =
  | { mode: "create" }
  | { mode: "edit"; cfg: CryptoGasConfig }
  | null;

function GasPoliciesPage() {
  const canConfig = useCanConfig();
  const networks = useQuery({
    queryKey: walletKeys.networksList(false),
    queryFn: () => fetchCryptoNetworks(),
  });
  const assets = useQuery({
    queryKey: walletKeys.assets(),
    queryFn: () => fetchCryptoAssets(),
  });
  const configs = useQuery({
    queryKey: walletKeys.gasConfigs(),
    queryFn: fetchGasConfigs,
  });
  const [editor, setEditor] = useState<EditorState>(null);

  const allNetworks = networks.data ?? [];
  const allAssets = assets.data ?? [];
  const rows = configs.data ?? [];
  const covered = new Set(rows.map((c) => c.network_id));

  const networkName = (id: string) =>
    allNetworks.find((n) => n.network_id === id)?.name || id;
  // Gas is paid in the network's native coin — that asset names the
  // units in the list and the editor.
  const nativeAsset = (networkID: string) =>
    allAssets.find((a) => a.network_id === networkID && a.kind === "NATIVE");

  const creatable = allNetworks.filter((n) => !covered.has(n.network_id));
  const uncoveredEnabled = allNetworks.filter(
    (n) => n.enabled && !covered.has(n.network_id),
  );
  const enabledCovered = allNetworks.filter(
    (n) => n.enabled && covered.has(n.network_id),
  ).length;
  const pausedCount = rows.filter((c) => !c.enabled).length;

  return (
    <div className="space-y-6">
      <PolicyPageHeader
        title="Gas funding policies"
        definition={
          <>
            Keeps deposit addresses topped up with the network's native coin
            so token sweeps never stall for lack of gas. One policy per
            network; a network without a policy is never topped up.
          </>
        }
        steps={[
          "scan deposit addresses",
          "native balance below the minimum?",
          "send the top-up from the gas wallet",
          "skip if the network fee exceeds the cap",
        ]}
      />

      <div className="space-y-3">
        <CoverageLine
          covered={enabledCovered}
          total={allNetworks.filter((n) => n.enabled).length}
          noun="enabled network"
          pausedCount={pausedCount}
        />
        <UncoveredCallout
          names={uncoveredEnabled.map((n) => n.name || n.network_id)}
          consequence="deposit addresses on these networks are never topped up, so token sweeps there can run out of gas."
        />
        <Card>
          <CardHeader className="border-b border-border">
            <CardTitle>Policies</CardTitle>
            {canConfig && (
              <Button size="sm" onClick={() => setEditor({ mode: "create" })}>
                New policy
              </Button>
            )}
          </CardHeader>
          <CardBody className="p-0">
            {configs.isLoading ? (
              <div className="space-y-2 p-5">
                <Skeleton className="h-10" />
                <Skeleton className="h-10" />
              </div>
            ) : configs.isError ? (
              <PolicyLoadError
                message={errMsg(configs.error)}
                onRetry={() => void configs.refetch()}
              />
            ) : rows.length === 0 ? (
              <EmptyState
                className="m-5"
                title="No gas funding policies yet"
                description="Deposit addresses hold users' tokens but pay their transaction fees in the network's native coin. Until a network has a policy, its addresses are never topped up."
                action={
                  canConfig && (
                    <Button
                      size="sm"
                      onClick={() => setEditor({ mode: "create" })}
                    >
                      New policy
                    </Button>
                  )
                }
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-border bg-surface-2">
                    <tr>
                      <th className={th}>Network</th>
                      <th className={th}>Rule</th>
                      <th className={th}>Status</th>
                      <th className={th}>Updated</th>
                      <th className={th} />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((c) => {
                      const asset = nativeAsset(c.network_id);
                      const dec = asset?.token_decimals ?? 18;
                      return (
                        <tr
                          key={c.network_id}
                          className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-surface-2"
                          onClick={() => setEditor({ mode: "edit", cfg: c })}
                        >
                          <td className={td}>
                            <div className="font-medium text-t-1">
                              {networkName(c.network_id)}
                            </div>
                            <div className="text-2xs text-muted">
                              funds with {asset?.symbol ?? "native coin"}
                            </div>
                          </td>
                          <td className={td}>
                            <span className="text-t-2">
                              below{" "}
                              <RuleAmount
                                base={c.min_base}
                                decimals={dec}
                                symbol={asset?.symbol}
                              />{" "}
                              → send{" "}
                              <RuleAmount
                                base={c.topup_base}
                                decimals={dec}
                                symbol={asset?.symbol}
                              />{" "}
                              · fee cap{" "}
                              <RuleAmount
                                base={c.max_fee_base}
                                decimals={dec}
                                symbol={asset?.symbol}
                                none="none"
                              />
                            </span>
                          </td>
                          <td className={td}>
                            <PolicyStatusBadge enabled={c.enabled} />
                          </td>
                          <td className={`${td} text-muted`}>
                            {c.updated_at ? fmtRelative(c.updated_at) : "—"}
                          </td>
                          <td className={`${td} text-right`}>
                            {canConfig && (
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditor({ mode: "edit", cfg: c });
                                }}
                              >
                                Edit
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
        <p className="text-2xs text-muted">
          Paused means the scanner skips the network — addresses there stop
          receiving top-ups until the policy is re-enabled. Funding runs
          appear under Custody → Gas funding.
        </p>
      </div>

      <GasPolicyEditor
        state={editor}
        creatable={creatable}
        nativeAsset={nativeAsset}
        networkName={networkName}
        onClose={() => setEditor(null)}
      />
    </div>
  );
}

function GasPolicyEditor({
  state,
  creatable,
  nativeAsset,
  networkName,
  onClose,
}: {
  state: EditorState;
  creatable: CryptoNetwork[];
  nativeAsset: (networkID: string) => CryptoAsset | undefined;
  networkName: (id: string) => string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const open = state !== null;
  const editing = state?.mode === "edit" ? state.cfg : null;
  const [draft, setDraft] = useState<CryptoGasConfig>(blankGasConfig());
  // Remount trick: key the modal body on the editor target so the draft
  // resets when a different row (or create) opens.
  const draftKey =
    state === null ? "closed" : state.mode === "create" ? "create" : state.cfg.network_id;
  const [lastKey, setLastKey] = useState("closed");
  if (draftKey !== lastKey) {
    setLastKey(draftKey);
    setDraft(editing ? { ...editing } : blankGasConfig());
  }

  const save = useMutation({
    mutationFn: (input: CryptoGasConfig) =>
      editing ? putGasConfig(input) : createGasConfig(input),
    onSuccess: async () => {
      onClose();
      await qc.invalidateQueries({ queryKey: walletKeys.gasConfigs() });
      await qc.invalidateQueries({
        queryKey: walletKeys.gasConfig(draft.network_id),
      });
    },
    meta: {
      successMessage: editing
        ? "Gas funding policy updated."
        : "Gas funding policy created.",
    },
  });

  const set = <K extends keyof CryptoGasConfig>(k: K, v: CryptoGasConfig[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const asset = nativeAsset(draft.network_id);
  const dec = asset?.token_decimals ?? 18;
  const canSubmit =
    draft.network_id !== "" && draft.min_base !== "" && draft.topup_base !== "";

  return (
    <FormModal
      open={open}
      title={
        editing
          ? `Edit gas funding policy — ${networkName(editing.network_id)}`
          : "New gas funding policy"
      }
      description={
        editing
          ? "Changes apply from the next scanner pass."
          : "Picks are limited to networks without a policy — each network has at most one."
      }
      confirmLabel={editing ? "Save changes" : "Create policy"}
      loading={save.isPending}
      error={save.isError ? errMsg(save.error) : null}
      canSubmit={canSubmit}
      onSubmit={() => save.mutate(draft)}
      onCancel={() => {
        onClose();
        save.reset();
      }}
    >
      <div className="space-y-4">
        {editing ? (
          <Input
            label="Network"
            value={`${networkName(editing.network_id)} (${editing.network_id})`}
            disabled
            hint="A policy stays with its network — create a separate policy for another one."
          />
        ) : (
          <Select
            label="Network"
            placeholder="Pick a network"
            value={draft.network_id}
            onChange={(e) => set("network_id", e.target.value)}
            options={creatable.map((n) => ({
              value: n.network_id,
              label: (n.name || n.network_id) + (n.enabled ? "" : " · network disabled"),
            }))}
            hint={
              creatable.length === 0
                ? "Every network already has a policy — edit one from the list instead."
                : "Networks that already have a policy aren't listed."
            }
          />
        )}
        <TokenAmountInput
          label="Fund when balance falls below"
          symbol={asset?.symbol}
          base={draft.min_base}
          decimals={dec}
          onBase={(b) => set("min_base", b)}
          emptyHint="Required — addresses under this native-coin balance get topped up."
          allowEmpty
        />
        <TokenAmountInput
          label="Send per top-up"
          symbol={asset?.symbol}
          base={draft.topup_base}
          decimals={dec}
          onBase={(b) => set("topup_base", b)}
          emptyHint="Required — each underfunded address receives this amount."
          allowEmpty
        />
        <TokenAmountInput
          label="Skip if network fee exceeds"
          symbol={asset?.symbol}
          base={draft.max_fee_base}
          decimals={dec}
          onBase={(b) => set("max_fee_base", b)}
          allowEmpty
          emptyHint="empty = no cap; top-ups go out whatever the fee."
        />
        <EnabledToggle
          checked={draft.enabled}
          onChange={(v) => set("enabled", v)}
        />
        <p className="text-2xs text-muted">
          Off = the scanner skips this network; the policy is kept but no
          top-ups are sent.
        </p>
      </div>
    </FormModal>
  );
}

function blankGasConfig(): CryptoGasConfig {
  return {
    network_id: "",
    min_base: "",
    topup_base: "",
    max_fee_base: "",
    enabled: true,
  };
}
